import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import {
  InstallationRecord,
  ServiceRecord,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { IconComponent } from '@ui/icon/icon.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { HttpResponse } from '@angular/common/http';

interface Attachment {
  subject_id: number;
  file_id: string;
  name: string;
  subject: string;
  uploaded_at: string;
}

@Component({
  selector: 'app-dossier-modal',
  standalone: true,
  imports: [ModalComponent, CommonModule, IconComponent],
  template: `
    <ui-modal
      [title]="item?.subject ?? 'Dossier'"
      [submitText]="'Sluiten'"
      [hideCancel]="true"
      (save)="closeModal()"
      (close)="closeModal()"
      [size]="'xl'"
    >
      <div class="space-y-4">
        <div class="grid grid-cols-1 gap-2">
          @if(item?.attachments?.length) {
          @for (attachment of item?.attachments; track attachment.file_id) {
          <div
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            <div class="flex items-center space-x-3">
              <ui-icon
                [name]="getFileIcon(attachment.name)"
                class="w-5 h-5 text-gray-500"
              />
              <span class="text-sm font-medium">{{ attachment.name }}</span>
            </div>
            <div class="flex space-x-2">
              <!-- <button
                (click)="openFile(attachment)"
                class="p-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <ui-icon name="eye" class="w-5 h-5" />
              </button> -->
              <button
                (click)="downloadFile(attachment)"
                class="p-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <ui-icon name="download" class="w-5 h-5" />
              </button>
            </div>
            </div>
            }
          } @else {
          <div class="text-center text-gray-500">Geen bijlagen gevonden</div>
          }
        </div>
      </div>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DossierModalComponent {
  @Input() item: InstallationRecord | ServiceRecord | null = null;
  @Input() assignmentId: string | null = null;
  private modalService = inject(ModalService);
  private modalRef = inject(MODAL_REF);
  private assignmentsService = inject(AssignmentsService);

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  openFile(attachment: Attachment) {
    // Implement file opening logic here
    // You might want to use a service to handle file viewing
    console.log('Opening file:', attachment);
  }

  downloadFile(attachment: Attachment) {
    if (!this.assignmentId) {
      return;
    }

    this.assignmentsService
      .downloadFile(this.assignmentId, attachment.subject_id.toString(), attachment.file_id)
      .subscribe((response: any) => {
        // Create blob from response
        const blob = new Blob([response], { type: this.getContentType(attachment.name) });

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name; // Set the download filename

        // Append to document, click and cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the blob URL
        window.URL.revokeObjectURL(url);
      });
  }

  private getContentType(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';

    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'zip':
        return 'application/zip';
      case 'rar':
        return 'application/x-rar-compressed';
      default:
        return 'application/octet-stream';
    }
  }

  getFileIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase() ?? '';

    switch (extension) {
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      case 'xls':
      case 'xlsx':
        return 'file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'file-image';
      case 'zip':
      case 'rar':
      case '7z':
        return 'file-archive';
      default:
        return 'file';
    }
  }
}
